import React, { useEffect, useMemo, useRef, useState } from "react";
import { Building2, Zap, MapPin, Image as ImageIcon } from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import Badge from "../common/Badge";
import BuyCredits from "./BuyCredits";
import { useToast } from "../../hooks/useToast";
import { LEAD_UNLOCK_COST } from "../../hooks/useCredits";
import { supabase } from "../../supabaseClient";
import { isLeadUnlocked } from "../../utils/leadAccess";
import { MARKETPLACE_ENABLED } from "../../featureFlags";

// ─── Module-level in-memory cache ─────────────────────────────────────────
const suburbCoordsCache = {};

// ─── Geocoding utility with Supabase cache ─────────────────────────────────
async function getSuburbCoords(suburb) {
  const key = suburb.trim().toLowerCase();

  if (key in suburbCoordsCache) return suburbCoordsCache[key];

  try {
    const { data } = await supabase
      .from("suburb_coords")
      .select("lat, lng")
      .eq("suburb", key)
      .maybeSingle();
    if (data) {
      const coords = { lat: Number(data.lat), lng: Number(data.lng) };
      suburbCoordsCache[key] = coords;
      return coords;
    }
  } catch {
    // fall through to Nominatim
  }

  try {
    const url =
      `https://nominatim.openstreetmap.org/search` +
      `?q=${encodeURIComponent(suburb + " Australia")}` +
      `&format=json&limit=1&countrycodes=au`;
    const res = await fetch(url, {
      headers: { "User-Agent": "RemoteQuotePro/1.0 (contact@remotequote.io)" },
    });
    if (!res.ok) throw new Error("Nominatim error");
    const json = await res.json();
    if (json && json[0]) {
      const coords = { lat: parseFloat(json[0].lat), lng: parseFloat(json[0].lon) };
      supabase
        .from("suburb_coords")
        .insert({ suburb: key, lat: coords.lat, lng: coords.lng })
        .then(() => {})
        .catch(() => {});
      suburbCoordsCache[key] = coords;
      return coords;
    }
  } catch {
    // Nominatim failed
  }

  suburbCoordsCache[key] = null;
  return null;
}

async function geocodeAddress(address) {
  try {
    const url =
      `https://nominatim.openstreetmap.org/search` +
      `?q=${encodeURIComponent(address)}` +
      `&format=json&limit=1&countrycodes=au`;
    const res = await fetch(url, {
      headers: { "User-Agent": "RemoteQuotePro/1.0 (contact@remotequote.io)" },
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (json && json[0]) {
      return { lat: parseFloat(json[0].lat), lng: parseFloat(json[0].lon) };
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Haversine distance in km ──────────────────────────────────────────────
function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const NOMINATIM_STAGGER_MS = 1100;

// ─── Tab definitions (4 tabs only) ────────────────────────────────────────
const TABS = [
  { key: "unlocked",  label: "Unlocked" },
  { key: "quoted",    label: "Quoted" },
  { key: "accepted",  label: "Accepted" },
  { key: "history",   label: "History" },
];

function normStatus(s) {
  return (s || "open").toLowerCase().replace(/\s+/g, "_");
}

function matchesTab(lead, tab, { unlockedLeadIds = [], businessId = null, dismissedLeadIds = null } = {}) {
  const s = normStatus(lead.status);
  const isUnlocked = isLeadUnlocked(lead, businessId, unlockedLeadIds);
  // Dismissals are now per-tradie (lead_dismissals table). The legacy
  // status==dismissed check is kept so any pre-migration rows that wrote
  // status='dismissed' globally still surface in History.
  const isDismissedByTradie = dismissedLeadIds?.has?.(lead.id) === true;
  const isDismissed = isDismissedByTradie || s === "dismissed" || s === "rejected";
  // A lead is "Quoted" / "Accepted" from this business's perspective when
  // a quote row exists for it (mapLeadRowsToRequests already scopes the
  // attached `lead.quote` to this business). Driving these tabs off the
  // quote row — not the lead.status text — means the categories are
  // immune to status casing and stay correct even if the lead status is
  // momentarily stale on the client.
  const customerStatus = (lead.quote?.customer_status || "").toLowerCase();
  const hasOpenQuote = !!lead.quote && customerStatus !== "accepted" && customerStatus !== "declined";
  const wasAccepted = customerStatus === "accepted";
  const wasDeclined = customerStatus === "declined";

  if (tab === "history")  return isDismissed || wasDeclined;
  if (tab === "unlocked") return isUnlocked && !isDismissed && !lead.quote;
  if (tab === "quoted")   return hasOpenQuote && !isDismissed;
  if (tab === "accepted") return wasAccepted;
  return false;
}

function ScoreBadge({ score }) {
  return (
    <span className={`score ${score >= 85 ? "good" : score >= 75 ? "mid" : "bad"}`}>
      Lead score {score}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────
export default function LeadMarketplace({
  requests,
  selected,
  onSelectLead,
  unlockedLeadIds,
  unlockLead,
  creditBalance,
  businessId,
  dismissLead,
  undismissLead,
  dismissedLeadIds,
  onSetDetailTab,
  onOpenBookModal,
  profileTradeTypes,
  profile,
  onGetNewJobsBlocked,
  canAccessLeads = true,
  isJobLimitReached = false,
  requestedTab = null,
  onTabApplied,
  isRead = () => false,
  onGoToSettings,
}) {
  const [activeTab, setActiveTab] = useState("unlocked");
  const [newJobsExpanded, setNewJobsExpanded] = useState(false);

  // Apply a programmatic tab switch (e.g. from TodayScreen "New job requests")
  useEffect(() => {
    if (requestedTab) {
      if (requestedTab === "get_new_jobs") {
        setNewJobsExpanded(true);
      } else {
        setActiveTab(requestedTab);
      }
      onTabApplied?.();
    }
  }, [requestedTab, onTabApplied]);

  const [sortBy, setSortBy] = useState("date");
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [radius, setRadius] = useState(200);
  const [coordsMap, setCoordsMap] = useState({});
  const [geocodingActive, setGeocodingActive] = useState(false);

  const tradieCoords     = useRef(null);
  const inFlightRef      = useRef(new Set());
  const pendingRef       = useRef(0);
  const geocodedSuburbs  = useRef(new Set());
  const { showToast } = useToast(); // eslint-disable-line no-unused-vars

  // Geocode tradie address + lead suburbs when the section is expanded
  useEffect(() => {
    if (!newJobsExpanded) return;

    const address = profile?.business_address;

    if (!tradieCoords.current && address && !inFlightRef.current.has("__tradie__")) {
      inFlightRef.current.add("__tradie__");
      geocodeAddress(address).then((coords) => {
        tradieCoords.current = coords;
        inFlightRef.current.delete("__tradie__");
        setCoordsMap((prev) => ({ ...prev, __tradie__: coords }));
      });
    }

    const suburbsToFetch = [
      ...new Set(
        requests
          .filter((l) => {
            const s = normStatus(l.status);
            const isUnlocked = isLeadUnlocked(l, businessId, unlockedLeadIds);
            return (
              ["open", "needs_review", "new_request", ""].includes(s) &&
              !isUnlocked &&
              l.suburb &&
              !geocodedSuburbs.current.has(l.suburb)
            );
          })
          .map((l) => l.suburb)
      ),
    ];

    if (suburbsToFetch.length === 0) return;

    pendingRef.current += suburbsToFetch.length;
    setGeocodingActive(true);

    suburbsToFetch.forEach((suburb, i) => {
      geocodedSuburbs.current.add(suburb);
      inFlightRef.current.add(suburb);
      setTimeout(() => {
        getSuburbCoords(suburb).then((coords) => {
          inFlightRef.current.delete(suburb);
          setCoordsMap((prev) => ({ ...prev, [suburb]: coords }));
          pendingRef.current -= 1;
          if (pendingRef.current <= 0) {
            pendingRef.current = 0;
            setGeocodingActive(false);
          }
        });
      }, i * NOMINATIM_STAGGER_MS);
    });
  }, [newJobsExpanded, profile?.business_address, requests, unlockedLeadIds, businessId]);

  // ── New jobs helpers ───────────────────────────────────────────────────
  function isNewJobBase(l) {
    const s = normStatus(l.status);
    const isUnlocked = isLeadUnlocked(l, businessId, unlockedLeadIds);
    if (!["open", "needs_review", "new_request", ""].includes(s) || isUnlocked) return false;
    // Per-tradie dismissals — drop the lead from Get New Jobs once this
    // business has dismissed it. The lead reappears for everyone else
    // (that's why dismissals live in lead_dismissals, not leads.status).
    if (dismissedLeadIds?.has?.(l.id)) return false;
    if (profileTradeTypes && profileTradeTypes.length > 0) {
      return profileTradeTypes.some(
        (t) => t.toLowerCase() === (l.trade || "").toLowerCase()
      );
    }
    return true;
  }

  // Badge count: trade-filtered only (no radius, no geocoding needed)
  const newJobsCount = requests.filter(isNewJobBase).length;

  // Expanded list: trade + radius filtered, then sorted
  const newJobsLeads = applySort(requests.filter((l) => {
    if (!isNewJobBase(l)) return false;
    if (profile?.business_address && tradieCoords.current && l.suburb) {
      const leadCoords = coordsMap[l.suburb];
      if (leadCoords) {
        const dist = haversineKm(
          tradieCoords.current.lat,
          tradieCoords.current.lng,
          leadCoords.lat,
          leadCoords.lng
        );
        if (dist > radius) return false;
      }
    }
    return true;
  }));

  const tabOptions = { unlockedLeadIds, businessId, dismissedLeadIds };

  function tabCount(tab) {
    return requests.filter((l) => matchesTab(l, tab, tabOptions)).length;
  }

  function applySort(leads) {
    const copy = [...leads];
    if (sortBy === "score")  copy.sort((a, b) => (b.score || 0) - (a.score || 0));
    if (sortBy === "date")   copy.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    if (sortBy === "suburb") copy.sort((a, b) => (a.suburb || "").localeCompare(b.suburb || ""));
    return copy;
  }

  const visibleLeads = applySort(requests.filter((l) => matchesTab(l, activeTab, tabOptions)));

  const { hasCompleteProfile, missingFields } = useMemo(() => {
    const abnOk = !!profile?.abn && /^\d{11}$/.test(String(profile.abn).replace(/\s+/g, ""));
    const tradesOk = Array.isArray(profile?.trade_types) && profile.trade_types.length > 0;
    const missing = [];
    if (!abnOk) missing.push("Add your ABN");
    if (!tradesOk) missing.push("Select at least one trade type");
    return { hasCompleteProfile: abnOk && tradesOk, missingFields: missing };
  }, [profile?.abn, profile?.trade_types]);

  // Radius status text + colour
  const noAddress = !profile?.business_address;
  const radiusStatus = noAddress
    ? "Add your address in Settings to filter by distance"
    : !tradieCoords.current
    ? "Locating your address…"
    : geocodingActive
    ? "Geocoding suburbs…"
    : "Location set";
  const radiusStatusColor = noAddress || geocodingActive
    ? "var(--theme-accent)"
    : "var(--text-muted, #6b7280)";

  // ── Get New Jobs card — clean, scannable layout for pre-unlock leads ──
  function renderNewJobCard(lead) {
    const photoCount = Math.max(lead.photos || 0, lead.form?.uploadedPhotos?.length || 0);
    let distanceKm = null;
    if (tradieCoords.current && lead.suburb) {
      const leadCoords = coordsMap[lead.suburb];
      if (leadCoords) {
        distanceKm = Math.round(
          haversineKm(
            tradieCoords.current.lat,
            tradieCoords.current.lng,
            leadCoords.lat,
            leadCoords.lng
          )
        );
      }
    }
    return (
      <div
        key={lead.id}
        className={`lead-card ${selected?.id === lead.id ? "selected-card" : ""}`}
        onClick={() => onSelectLead(lead.id)}
        style={{ display: "flex", flexDirection: "column", gap: "10px" }}
      >
        {/* Top row: suburb + postcode prominent, score badge top right */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "1.05rem",
              fontWeight: 750,
              color: "var(--text, #1C1C1E)",
              letterSpacing: "-0.01em",
            }}>
              <MapPin size={16} style={{ flexShrink: 0, color: "var(--theme-accent, #FF8A00)" }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {lead.suburb || "Unknown"}{lead.postcode ? ` ${lead.postcode}` : ""}
              </span>
            </div>
            <div style={{
              marginTop: "4px",
              fontSize: "0.85rem",
              color: "var(--text-soft, #6b5448)",
              fontWeight: 500,
            }}>
              {lead.trade}{lead.type ? ` · ${lead.type}` : ""}
            </div>
          </div>
          <ScoreBadge score={lead.score} />
        </div>

        {/* Meta row: photos · distance · be-first badge */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center", fontSize: "0.78rem", color: "var(--text-soft, #6b5448)" }}>
          {photoCount > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              <ImageIcon size={14} /> {photoCount}
            </span>
          )}
          {distanceKm !== null && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              <MapPin size={13} /> {distanceKm} km away
            </span>
          )}
          {lead.soldCount === 0 && !lead.soldOut && (
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 9px",
              borderRadius: "999px",
              background: "rgba(34,197,94,0.12)",
              color: "#15803d",
              fontWeight: 600,
              fontSize: "0.74rem",
            }}>
              Be first to quote
            </span>
          )}
          {lead.soldOut && (
            <span style={{
              display: "inline-flex",
              padding: "2px 9px",
              borderRadius: "999px",
              background: "rgba(0,0,0,0.07)",
              color: "var(--text-muted, #6b7280)",
              fontWeight: 600,
              fontSize: "0.74rem",
            }}>
              Fully quoted
            </span>
          )}
        </div>

        {/* Scope summary */}
        {lead.areas && (
          <div style={{ fontSize: "0.85rem", color: "var(--text-soft, #6b5448)", lineHeight: 1.4 }}>
            {lead.areas}
          </div>
        )}

        {/* Action row: Unlock primary, Dismiss ghost */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "2px", flexWrap: "wrap" }}>
          {!lead.soldOut && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); unlockLead(lead.id); }}
              style={{
                background: "var(--theme-primary-gradient, var(--theme-accent, #FF8A00))",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                padding: "10px 18px",
                fontWeight: 700,
                fontSize: "0.9rem",
                cursor: "pointer",
                boxShadow: "0 4px 14px var(--theme-primary-shadow, rgba(255, 138, 0,0.28))",
                flexShrink: 0,
              }}
            >
              Unlock — {LEAD_UNLOCK_COST} credit{LEAD_UNLOCK_COST === 1 ? "" : "s"}
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); dismissLead?.(lead.id); }}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-muted, #6b7280)",
              fontSize: "0.8rem",
              fontWeight: 500,
              cursor: "pointer",
              padding: "8px 4px",
              textDecoration: "underline",
              textUnderlineOffset: "3px",
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // ── Pipeline card for unlocked leads — clean, scannable layout ────────
  function renderUnlockedCard(lead) {
    const isDirect = !!(businessId && lead.directBusinessId === businessId);
    const photoCount = Math.max(lead.photos || 0, lead.form?.uploadedPhotos?.length || 0);
    const customerStatus = (lead.quote?.customer_status || "").toLowerCase();
    const acceptedQuote = customerStatus === "accepted";
    const declinedQuote = customerStatus === "declined";
    const hasQuote = !!lead.quote;
    // Status badge — single source of truth, prefers the most-progressed
    // state (Accepted > Declined > Quoted > Unlocked).
    let statusLabel;
    let statusStyle;
    if (acceptedQuote) {
      statusLabel = "Accepted";
      statusStyle = { background: "rgba(34,197,94,0.12)", color: "#15803d", border: "1px solid rgba(34,197,94,0.32)" };
    } else if (declinedQuote) {
      statusLabel = "Declined";
      statusStyle = { background: "rgba(220,38,38,0.1)", color: "#b91c1c", border: "1px solid rgba(220,38,38,0.28)" };
    } else if (hasQuote) {
      statusLabel = "Quoted";
      statusStyle = { background: "rgba(234,179,8,0.14)", color: "#854d0e", border: "1px solid rgba(234,179,8,0.4)" };
    } else {
      statusLabel = isDirect ? "Direct" : "Unlocked";
      statusStyle = { background: "rgba(59,130,246,0.1)", color: "#1d4ed8", border: "1px solid rgba(59,130,246,0.28)" };
    }

    const customerName = lead.customer || (lead.detailsLoaded ? "Unnamed customer" : "Loading…");
    // First line of the customer's free-text description, trimmed to one line
    // by max-height + line-clamp via inline -webkit clamp (no new CSS class).
    const descriptionSnippet = (lead.areas || lead.form?.notes || "").trim();
    const msgUnread = !isRead(lead.id);

    return (
      <div
        key={lead.id}
        className={`lead-card ${selected?.id === lead.id ? "selected-card" : ""}`}
        onClick={() => onSelectLead(lead.id)}
        style={{ display: "flex", flexDirection: "column", gap: "8px" }}
      >
        {/* Top row: customer name + status badge */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: "1.05rem",
              fontWeight: 750,
              color: "var(--text, #1C1C1E)",
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {customerName}
            </div>
            <div style={{ marginTop: "3px", fontSize: "0.85rem", color: "var(--text, #1C1C1E)", fontWeight: 600 }}>
              {lead.suburb || "Unknown"}{lead.postcode ? ` ${lead.postcode}` : ""}
            </div>
            <div style={{ marginTop: "2px", fontSize: "0.8rem", color: "var(--text-soft, #6b5448)" }}>
              {lead.trade}{lead.type ? ` · ${lead.type}` : ""}
            </div>
          </div>
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 10px",
            borderRadius: "999px",
            fontSize: "0.72rem",
            fontWeight: 700,
            whiteSpace: "nowrap",
            flexShrink: 0,
            ...statusStyle,
          }}>
            {statusLabel}
          </span>
        </div>

        {/* Meta: photo count + deposit pill (when relevant) */}
        {(photoCount > 0 || lead.quote?.deposit_paid) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", fontSize: "0.78rem", color: "var(--text-soft, #6b5448)" }}>
            {photoCount > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <ImageIcon size={14} /> {photoCount}
              </span>
            )}
            {lead.quote?.deposit_paid && (
              <span style={{
                display: "inline-flex",
                padding: "2px 8px",
                borderRadius: "999px",
                background: "rgba(34,197,94,0.12)",
                color: "#15803d",
                fontWeight: 600,
                fontSize: "0.72rem",
              }}>
                Deposit paid
              </span>
            )}
          </div>
        )}

        {/* Description snippet (one line) */}
        {descriptionSnippet && (
          <div
            style={{
              fontSize: "0.82rem",
              color: "var(--text-soft, #6b5448)",
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {descriptionSnippet}
          </div>
        )}

        {/* Quick action tabs — Messages always orange when unread */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "2px" }}>
          {["details", "quote", "messages", "media"].map((tab) => {
            const isMsgPill = tab === "messages";
            const isQuotePill = tab === "quote";
            const isMediaPill = tab === "media";
            const isUnreadMsg = isMsgPill && msgUnread;
            let pillStyle;
            if (isQuotePill) {
              pillStyle = hasQuote
                ? { border: "1px solid rgba(34,197,94,0.35)", background: "rgba(34,197,94,0.1)", color: "#15803d", fontWeight: 600 }
                : { border: "1px solid rgba(234,179,8,0.4)", background: "rgba(234,179,8,0.12)", color: "#854d0e", fontWeight: 600 };
            } else if (isUnreadMsg) {
              pillStyle = { border: "1px solid #FF8A00", background: "rgba(255, 138, 0,0.08)", color: "var(--primary)", fontWeight: 600 };
            } else {
              pillStyle = { border: "1px solid var(--border, #e5e7eb)", background: "transparent", color: "var(--text-soft, #6b5448)", fontWeight: 500 };
            }
            return (
              <button
                key={tab}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectLead(lead.id);
                  onSetDetailTab?.(tab);
                }}
                style={{
                  padding: "5px 11px",
                  borderRadius: "999px",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  textTransform: "capitalize",
                  ...pillStyle,
                }}
              >
                {isQuotePill ? (hasQuote ? "✓ Quoted" : "Quote") : isMediaPill ? "Media" : tab}
              </button>
            );
          })}
        </div>

        {/* Book-date / Booked indicator for accepted leads */}
        {acceptedQuote && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "2px" }}>
            {lead.jobBooked ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#22C55E", color: "#fff", borderRadius: "999px", padding: "3px 10px", fontSize: "0.78rem", fontWeight: 600 }}>
                ✓ Job booked
              </span>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenBookModal?.(lead); }}
                style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "var(--theme-accent)", color: "#fff", border: "none", borderRadius: "999px", padding: "4px 12px", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Book date
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Shared lead card renderer ──────────────────────────────────────────
  function renderLeadCard(lead, isNewJobs = false) {
    if (isNewJobs) return renderNewJobCard(lead);
    const isUnlocked = isLeadUnlocked(lead, businessId, unlockedLeadIds);
    const isDirect = !!(businessId && lead.directBusinessId === businessId);
    const isDismissed =
      dismissedLeadIds?.has?.(lead.id) === true ||
      ["dismissed", "rejected"].includes(normStatus(lead.status));
    // For unlocked leads use the new dedicated layout — no duplicate suburb
    // text, photo-icon meta, and consistent Messages-tab unread styling.
    if (isUnlocked && !isDismissed) return renderUnlockedCard(lead);

    return (
      <div
        key={lead.id}
        className={`lead-card ${selected?.id === lead.id ? "selected-card" : ""}`}
        onClick={() => onSelectLead(lead.id)}
      >
        <div className="lead-top">
          <div>
            <div className="lead-name">
              {isUnlocked
                ? lead.customer
                  ? lead.customer
                  : <span style={{ color: "var(--text-muted, #6b7280)", fontSize: "0.85rem", fontWeight: 400 }}>{lead.trade}{lead.suburb ? ` · ${lead.suburb}` : ""}</span>
                : <span style={{ color: "var(--text-faint, #9ca3af)", fontStyle: "italic", fontSize: "0.85rem", fontWeight: 400 }}>🔒 Unlock to reveal name</span>
              }
            </div>
            <div className="muted small">
              {lead.trade} - {lead.type} ~ {lead.suburb}{lead.postcode ? ` ${lead.postcode}` : ""}
            </div>
          </div>
          <ScoreBadge score={lead.score} />
        </div>

        <div className="pill-row">
          <Badge>{lead.trade}</Badge>
          <Badge>{lead.status}</Badge>
          <Badge>
            {Math.max(lead.photos || 0, lead.form?.uploadedPhotos?.length || 0)}{" "}
            photos
          </Badge>
          <Badge type={lead.soldOut ? "default" : lead.soldCount === 0 ? "success" : lead.remainingSlots === 1 ? "error" : "info"}>
            {lead.soldOut ? "Fully quoted" : lead.soldCount === 0 ? "Be first to quote" : lead.soldCount === 1 ? "1 tradie quoting" : `Last spot — ${lead.remainingSlots} left`}
          </Badge>
          {isDirect && <Badge type="success">Direct</Badge>}
          {lead.quote?.deposit_paid && <Badge type="success">Deposit paid</Badge>}
        </div>

        <div className="muted">{lead.areas}</div>

        <div className="button-row">
          {isDismissed ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); undismissLead?.(lead.id); }}
              style={{
                padding: "8px 14px",
                borderRadius: "999px",
                border: "1px solid var(--border, #e5e7eb)",
                background: "transparent",
                color: "var(--text-soft, #6b5448)",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reopen
            </button>
          ) : isUnlocked ? (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "0.7rem", color: "var(--text-muted, #9ca3af)", fontWeight: 500, marginRight: "2px" }}>
                ✓ {isDirect ? "Direct" : "Unlocked"}
              </span>
              {["details", "quote", "messages", "media"].map((tab) => {
                const isMsgPill   = tab === "messages";
                const isQuotePill = tab === "quote";
                const msgUnread   = isMsgPill && !isRead(lead.id);
                const hasQuote    = isQuotePill && !!lead.quote;

                let pillStyle;
                if (isQuotePill) {
                  pillStyle = hasQuote
                    ? { border: "1px solid rgba(34,197,94,0.35)",  background: "rgba(34,197,94,0.1)",   color: "#15803d", fontWeight: 600 }
                    : { border: "1px solid rgba(234,179,8,0.4)",   background: "rgba(234,179,8,0.12)",  color: "#854d0e", fontWeight: 600 };
                } else if (msgUnread) {
                  pillStyle = { border: "1px solid #FF8A00", background: "rgba(255, 138, 0,0.08)", color: "var(--primary)", fontWeight: 600 };
                } else {
                  pillStyle = { border: "1px solid var(--border, #e5e7eb)", background: "transparent", color: "var(--text-soft, #6b5448)", fontWeight: 500 };
                }

                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectLead(lead.id);
                      onSetDetailTab?.(tab);
                    }}
                    style={{
                      padding: "5px 11px",
                      borderRadius: "999px",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      textTransform: "capitalize",
                      ...pillStyle,
                    }}
                  >
                    {isQuotePill ? (hasQuote ? "✓ Quoted" : "Quote") : tab}
                  </button>
                );
              })}
            </div>
          ) : lead.soldOut ? (
            <Badge>Sold out</Badge>
          ) : (
            <>
              <Button
                secondary
                onClick={(e) => {
                  e.stopPropagation();
                  unlockLead(lead.id);
                }}
              >
                Unlock ~ {LEAD_UNLOCK_COST} credit
              </Button>
              {isNewJobs && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); dismissLead?.(lead.id); }}
                  style={{
                    padding: "8px 14px",
                    borderRadius: "999px",
                    border: "1px solid var(--border, #e5e7eb)",
                    background: "transparent",
                    color: "var(--text-soft, #6b5448)",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Dismiss
                </button>
              )}
            </>
          )}

          {(lead.status || "").toLowerCase() === "accepted" && (
            lead.jobBooked ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#22C55E", color: "#fff", borderRadius: "999px", padding: "3px 10px", fontSize: "0.78rem", fontWeight: 600 }}>
                ✓ Job booked
              </span>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenBookModal?.(lead); }}
                style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "var(--theme-accent)", color: "#fff", border: "none", borderRadius: "999px", padding: "4px 12px", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Book date
              </button>
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <>
    {MARKETPLACE_ENABLED && showBuyCredits && (
      <BuyCredits
        businessId={businessId}
        creditBalance={creditBalance}
        onClose={() => setShowBuyCredits(false)}
      />
    )}
    <Card>
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="dashboard-top">
        <div>
          <h2>
            <Building2 size={18} /> Quote pipeline
          </h2>
          {MARKETPLACE_ENABLED && (
          <p className="muted" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <strong>Credits:</strong> {creditBalance.toLocaleString()}
            <button
              type="button"
              className="link-btn"
              onClick={() => setShowBuyCredits(true)}
              style={{ fontSize: "0.8rem" }}
            >
              <Zap size={12} style={{ display: "inline", verticalAlign: "middle" }} /> Top up credits
            </button>
          </p>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
          <span style={{ fontSize: "0.78rem", color: "var(--text-muted, #6b7280)", whiteSpace: "nowrap" }}>
            Sort:
          </span>
          {["date", "score", "suburb"].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setSortBy(opt)}
              style={{
                padding: "4px 10px",
                borderRadius: "999px",
                border: "1px solid var(--border, #e5e7eb)",
                background: sortBy === opt ? "var(--dark)" : "none",
                color: sortBy === opt ? "#fff" : "var(--text-muted, #6b7280)",
                fontSize: "0.78rem",
                fontWeight: sortBy === opt ? 700 : 400,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "background 0.15s, color 0.15s",
              }}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Get New Jobs collapsible section ─────────────────────── */}
      {MARKETPLACE_ENABLED && (
      <div style={{ marginBottom: "12px" }}>
        <button
          type="button"
          onClick={() => {
            if (!newJobsExpanded && isJobLimitReached) {
              onGetNewJobsBlocked?.();
              return;
            }
            setNewJobsExpanded((v) => !v);
          }}
          style={{
            width: "100%",
            background: "var(--theme-accent-soft)",
            border: "1px solid var(--theme-accent-muted)",
            borderRadius: "12px",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <span style={{ flex: 1, display: "flex", alignItems: "center", gap: "8px" }}>
            <Zap size={15} style={{ flexShrink: 0, color: "var(--theme-accent)" }} />
            <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--text, #1C1C1E)" }}>
              Get New Jobs
            </span>
            <span
              style={{
                background: newJobsCount > 0 ? "var(--theme-accent)" : "rgba(0,0,0,0.12)",
                color: newJobsCount > 0 ? "#fff" : "var(--text-muted, #6b7280)",
                borderRadius: "999px",
                padding: "1px 8px",
                fontSize: "0.72rem",
                fontWeight: 700,
                minWidth: "20px",
                textAlign: "center",
              }}
            >
              {newJobsCount}
            </span>
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            style={{
              flexShrink: 0,
              transition: "transform 0.2s",
              transform: newJobsExpanded ? "rotate(180deg)" : "rotate(0deg)",
              color: "var(--text-muted, #6b7280)",
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {newJobsExpanded && (
          <div
            style={{
              border: "1px solid var(--theme-accent-muted)",
              borderTop: "none",
              borderRadius: "0 0 12px 12px",
              padding: "12px",
            }}
          >
            {/* Radius filter */}
            <div
              style={{
                paddingBottom: "10px",
                borderBottom: "1px solid var(--border, #e5e7eb)",
                marginBottom: "10px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ fontSize: "0.82rem", color: "var(--text-muted, #6b7280)", whiteSpace: "nowrap" }}>
                  Within{" "}
                  <strong style={{ color: "var(--text, #1C1C1E)" }}>{radius} km</strong>
                </span>
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: radiusStatusColor,
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  {geocodingActive && (
                    <span
                      style={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "var(--theme-accent)",
                        animation: "pulse 1s ease-in-out infinite",
                      }}
                    />
                  )}
                  {radiusStatus}
                </span>
              </div>
              <input
                type="range"
                min={5}
                max={1000}
                step={25}
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                style={{ width: "100%", display: "block", accentColor: "var(--theme-accent)" }}
              />
            </div>

            {/* Lead list */}
            {!hasCompleteProfile ? (
              <div
                style={{
                  padding: "20px 16px",
                  border: "1px solid var(--border, #e5e7eb)",
                  borderRadius: "12px",
                  background: "var(--card-bg, #fff)",
                }}
              >
                <p style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--text, #1C1C1E)", margin: "0 0 8px" }}>
                  Complete your profile to access new jobs
                </p>
                <p className="muted small" style={{ margin: "0 0 12px" }}>
                  Customers need to trust who they're inviting to quote. Add the missing details to unlock the job feed.
                </p>
                <ul style={{ margin: "0 0 16px", paddingLeft: "18px" }}>
                  {missingFields.map((f) => (
                    <li key={f} className="muted small" style={{ marginBottom: "4px" }}>{f}</li>
                  ))}
                </ul>
                <Button onClick={() => onGoToSettings?.()}>Complete profile</Button>
              </div>
            ) : newJobsLeads.length === 0 ? (
              <p className="muted" style={{ textAlign: "center", padding: "20px 0" }}>
                No new jobs match your trade and location.
              </p>
            ) : (
              <div className="lead-list">
                {newJobsLeads.map((lead) => renderLeadCard(lead, true))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* ── Pipeline section divider ─────────────────────────────── */}
      <div
        style={{
          marginTop: "20px",
          paddingTop: "16px",
          borderTop: "1px solid var(--border, #e5e7eb)",
        }}
      >
        <h3
          style={{
            margin: "0 0 10px",
            fontSize: "0.95rem",
            fontWeight: 700,
            color: "var(--text, #1C1C1E)",
          }}
        >
          Your pipeline
        </h3>
      </div>

      {/* ── 4-tab bar ────────────────────────────────────────────── */}
      <style>{`
        @keyframes rqp-dot-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>
      <div
        style={{
          display: "flex",
          padding: "8px 0",
          borderBottom: "1px solid var(--border, #e5e7eb)",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            background: "rgba(0,0,0,0.05)",
            borderRadius: "999px",
            padding: "4px",
            gap: "2px",
          }}
        >
          {TABS.map(({ key, label }) => {
            const isActive = activeTab === key;
            const count = tabCount(key);

            if (isActive) {
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    background: "var(--dark)",
                    border: "none",
                    borderRadius: "999px",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    transition: "background 0.15s",
                  }}
                >
                  {label}
                  <span
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      color: "#fff",
                      borderRadius: "999px",
                      padding: "1px 6px",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      minWidth: "18px",
                      textAlign: "center",
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            }

            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                style={{
                  flex: 1,
                  padding: "6px 8px",
                  background: "none",
                  border: "none",
                  borderRadius: "999px",
                  color: "var(--text-soft, #6b5448)",
                  fontWeight: 400,
                  fontSize: "0.82rem",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  transition: "color 0.15s",
                }}
              >
                {label}
                <span
                  style={{
                    background: "rgba(0,0,0,0.08)",
                    color: "var(--text-soft, #6b5448)",
                    borderRadius: "999px",
                    padding: "1px 6px",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    minWidth: "18px",
                    textAlign: "center",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Lead list ────────────────────────────────────────────── */}
      <div style={{ marginTop: "12px" }}>
        {visibleLeads.length === 0 ? (
          <p className="muted" style={{ textAlign: "center", padding: "32px 0" }}>
            No leads here yet.
          </p>
        ) : (
          <div className="lead-list">
            {visibleLeads.map(renderLeadCard)}
          </div>
        )}
      </div>
    </Card>
    </>
  );
}
